import { useState, useEffect } from 'react';

export default function FloatingCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      try {
        setIsVisible(window.scrollY > 600);
      } catch (e) {
        console.error('Error handling scroll event:', e);
        setIsVisible(false);
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible || isDismissed) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 animate-[slideUp_0.4s_ease-out]"
    >
      {/* 閉じるボタン */}
      <button
        onClick={() => setIsDismissed(true)}
        className="absolute -top-8 right-3 md:right-4 w-7 h-7 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors cursor-pointer group"
        aria-label="閉じる"
      >
        <i className="ri-close-line text-sm text-gray-500 group-hover:text-gray-800"></i>
      </button>

      {/* メインバナー */}
      <div className="bg-[#FFFDF8] border-t-2 border-[#4A8B3F] shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
        {/* モバイル: 縦積みレイアウト */}
        <div className="block md:hidden px-3 py-2.5">
          <div className="flex items-center justify-between gap-2 mb-2">
            {/* 数量限定バッジ */}
            <span className="relative flex-shrink-0">
              <span className="inline-flex items-center px-2 py-0.5 bg-[#2C2C2C] text-white text-[9px] font-bold rounded-md tracking-wider whitespace-nowrap">
                <i className="ri-fire-fill text-[#F5A623] mr-0.5 text-[10px]"></i>
                数量限定
              </span>
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#E53935] rounded-full animate-ping"></span>
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#E53935] rounded-full"></span>
            </span>
            {/* 割引テキスト */}
            <div className="flex items-baseline gap-1">
              <span className="text-[#C8956C] text-[10px] font-bold whitespace-nowrap">早割</span>
              <span
                className="text-[#E53935] text-xl font-black leading-none tracking-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                最大25<span className="text-sm">%</span>OFF
              </span>
            </div>
            {/* 補足テキスト */}
            <p className="text-[9px] text-[#6B6B6B] leading-tight flex-shrink-0">
              <strong className="text-[#2C2C2C]">Makuake公開日を優先通知</strong>
            </p>
          </div>
          {/* LINEボタン - フル幅 */}
          <a
            href="https://lin.ee/d6PoCFQ6"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="group relative flex items-center justify-center gap-2 w-full py-2.5 bg-[#06C755] text-white font-bold text-sm rounded-full hover:bg-[#05B34C] transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer shadow-[0_4px_16px_rgba(6,199,85,0.35)] hover:shadow-[0_6px_24px_rgba(6,199,85,0.5)]"
          >
            <svg
              className="relative w-5 h-5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
            </svg>
            <span className="relative">公開日の優先案内を受取る</span>
            <i className="ri-arrow-right-line text-base transition-transform duration-300 group-hover:translate-x-1"></i>
          </a>
        </div>

        {/* デスクトップ: 横並びレイアウト */}
        <div className="hidden md:flex max-w-4xl mx-auto px-4 py-3 items-center gap-6">
          {/* 左: 数量限定バッジ + テキスト */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="relative flex-shrink-0">
                <span className="inline-flex items-center px-2.5 py-1 bg-[#2C2C2C] text-white text-[10px] font-bold rounded-md tracking-wider whitespace-nowrap">
                  <i className="ri-fire-fill text-[#F5A623] mr-1 text-xs"></i>
                  数量限定
                </span>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#E53935] rounded-full animate-ping"></span>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#E53935] rounded-full"></span>
              </span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-[#C8956C] text-xs font-bold whitespace-nowrap">早割</span>
                <span
                  className="text-[#E53935] text-3xl font-black leading-none tracking-tight"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  最大25<span className="text-xl">%</span>OFF
                </span>
              </div>
            </div>
            <p className="text-xs text-[#6B6B6B] leading-snug">
              <strong className="text-[#E53935]">Makuake公開日</strong>の販売開始時刻・早割価格を
              <br />
              <strong className="text-[#2C2C2C]">LINEで誰よりも早くお届け</strong>
            </p>
          </div>

          {/* 右: LINE CTAボタン */}
          <div className="flex-shrink-0 flex flex-col items-center gap-1">
            <a
              href="https://lin.ee/d6PoCFQ6"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group relative inline-flex items-center gap-2.5 px-8 py-3.5 bg-[#06C755] text-white font-bold text-base rounded-full hover:bg-[#05B34C] transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer shadow-[0_4px_20px_rgba(6,199,85,0.35)] hover:shadow-[0_8px_32px_rgba(6,199,85,0.5)]"
            >
              <svg
                className="relative w-6 h-6 flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
              </svg>
              <span className="relative">公開日の優先案内を受取る</span>
              <i className="ri-arrow-right-line text-lg transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
            <p className="text-[9px] text-[#9B9B9B]">
              ※LINE登録で Makuake公開日に優先通知が届きます
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes gentlePulse {
          0%, 100% { transform: scale(1); opacity: 0.2; }
          50% { transform: scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
